import Message from 'model/MessageTypes/Messages';
import MessageResponse from 'model/MessageTypes/Responses';
import convertResponseToMessage from 'model/MessageTypes/convertResponseToMessage';

import { CombinedActionTypes } from '../../actionsFactory';

export interface State {
  [contactId: string]: {
    messages: Message[];
    total: number;
    loading: boolean;
    error: any;
  };
}

const initialEntry = {
  messages: [],
  total: 0,
  loading: false,
  error: null,
};

export const initState: State = {};

const prependOrReplace = (
  currentHits: Message[],
  newHits: Message[],
): Message[] => [
  ...newHits.filter(
    (hit) =>
      !currentHits.some(
        (currentHit) => currentHit.message.id === hit.message.id,
      ),
  ),
  ...currentHits.map(
    (hit) =>
      newHits.find((newHit) => newHit.message.id === hit.message.id) || hit,
  ),
];

const appendOrReplace = (
  currentHits: Message[],
  newHits: Message[],
): Message[] => [
  ...currentHits.map(
    (hit) =>
      newHits.find((newHit) => newHit.message.id === hit.message.id) || hit,
  ),
  ...newHits.filter(
    (hit) =>
      !currentHits.some(
        (currentHit) => currentHit.message.id === hit.message.id,
      ),
  ),
];

const replaceMessage = (
  messages: Message[],
  message: Message,
  id: string,
): Message[] => {
  const index = messages.findIndex((m) => m.message.id === id);
  if (index !== -1) {
    messages[index] = message;
  } else {
    return [message, ...messages];
  }
  return messages;
};

const sortMessagesByCreatedAt = (messages: Message[]): Message[] => {
  return messages.concat().sort((messageA, messageB) => {
    return messageB.message.created_at - messageA.message.created_at;
  });
};

const hitsToMessages = (hits: MessageResponse[]): Message[] =>
  hits.map((hit) => convertResponseToMessage(hit));

const reducer = (state: State, action: CombinedActionTypes): State => {
  switch (action.type) {
    case 'MESSAGES:FETCHING':
      return {
        ...state,
        [action.payload.contactId]: {
          ...initialEntry,
          ...state[action.payload.contactId],
          loading: true,
          error: null,
        },
      };
    case 'MESSAGES:FETCHING_ERROR':
      return {
        ...state,
        [action.payload.contactId]: {
          ...state[action.payload.contactId],
          loading: false,
          error: action.payload.error,
        },
      };
    case 'MESSAGES:FETCHING_SUCCESS':
      return {
        ...state,
        [action.payload.contactId]: {
          ...state[action.payload.contactId],
          messages: sortMessagesByCreatedAt(
            appendOrReplace(
              state[action.payload.contactId]?.messages ?? [],
              hitsToMessages(action.payload.result.hits),
            ),
          ),
          total: action.payload.result.total,
          loading: false,
          error: null,
        },
      };
    case 'MESSAGES:SEND_MESSAGE':
      return {
        ...state,
        [action.payload.message.to]: {
          ...state[action.payload.message.to],
          messages: sortMessagesByCreatedAt([
            action.payload,
            ...(state[action.payload.message.to]?.messages ?? []),
          ]),
        },
      };
    case 'MESSAGES:SEND_MESSAGE_ERROR':
      return {
        ...state,
        [action.payload.to]: {
          ...state[action.payload.to],
          messages: sortMessagesByCreatedAt(
            state[action.payload.to].messages.filter((message) => {
              return message.message.id !== action.payload.tempId;
            }),
          ),
        },
      };
    case 'MESSAGES:SEND_MESSAGE_SUCCESS':
      return {
        ...state,
        [action.payload.message.to]: {
          ...state[action.payload.message.to],
          messages: sortMessagesByCreatedAt(
            replaceMessage(
              state[action.payload.message.to]?.messages ?? [],
              action.payload,
              action.payload.tempId,
            ),
          ),
        },
      };
    case 'MESSAGES:NEW_MESSAGE':
      return {
        ...state,
        [action.payload.from]: {
          ...initialEntry,
          ...state[action.payload.from],
          messages: sortMessagesByCreatedAt(
            prependOrReplace(state[action.payload.from]?.messages ?? [], [
              convertResponseToMessage(action.payload),
            ]),
          ),
        },
      };
    case 'MESSAGES:MARK_AS_READ':
      if (!state[action.payload.contactId]) {
        return state;
      }
      return {
        ...state,
        [action.payload.contactId]: {
          ...state[action.payload.contactId],
          messages: sortMessagesByCreatedAt(
            state[action.payload.contactId].messages.map<Message>(
              (message) =>
                ({
                  ...message,
                  message: {
                    ...message.message,
                    status:
                      message.message.from !== action.payload.accountId
                        ? 'READ'
                        : message.message.status,
                  },
                }) as Message,
            ),
          ),
        },
      };
    case 'MESSAGES:MARK_OWN_AS_READ':
      if (!state[action.payload.contactId]) {
        return state;
      }
      return {
        ...state,
        [action.payload.contactId]: {
          ...state[action.payload.contactId],
          messages: sortMessagesByCreatedAt(
            state[action.payload.contactId].messages.map<Message>(
              (message) =>
                ({
                  ...message,
                  message: { ...message.message, status: 'READ' },
                }) as Message,
            ),
          ),
        },
      };
    default:
      return state;
  }
};

export default reducer;
export { initialEntry };
